import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { UI_NAMESPACE } from '../../i18n';
import { cn } from '../../lib/utils';
import { useRestoreFocusOnClose } from '../../lib/hooks/use-restore-focus-on-close';
import { useKeepFocusInContent } from '../../lib/hooks/use-keep-focus-in-content';
import { XCloseIcon } from '../molecules/x-close-icon';
function Sheet({ ...props }) {
    return _jsx(SheetPrimitive.Root, { "data-slot": "sheet", ...props });
}
function SheetTrigger({ ...props }) {
    return _jsx(SheetPrimitive.Trigger, { "data-slot": "sheet-trigger", ...props });
}
function SheetClose({ ...props }) {
    return _jsx(SheetPrimitive.Close, { "data-slot": "sheet-close", ...props });
}
function SheetPortal({ ...props }) {
    return _jsx(SheetPrimitive.Portal, { "data-slot": "sheet-portal", ...props });
}
function SheetOverlay({ className, disableOpenAnimation = false, disableCloseAnimation = false, ...props }) {
    return (_jsx(SheetPrimitive.Overlay, { "data-slot": "sheet-overlay", className: cn('fixed inset-0 z-50 bg-black/80', !disableOpenAnimation &&
            'data-[state=open]:animate-in data-[state=open]:fade-in-0', !disableCloseAnimation &&
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0', className), ...props }));
}
function SheetContent({ className, children, side = 'right', hideCloseButton = false, describedBy = 'none', disableOpenAnimation = false, disableCloseAnimation = false, onOpenAutoFocus, onCloseAutoFocus, ref, ...props }) {
    const { t } = useTranslation(UI_NAMESPACE);
    const focusHandlers = useRestoreFocusOnClose({
        onOpenAutoFocus,
        onCloseAutoFocus,
    });
    const contentRef = useKeepFocusInContent(ref);
    const sideAnimations = {
        right: {
            open: 'data-[state=open]:slide-in-from-right',
            close: 'data-[state=closed]:slide-out-to-right',
        },
        left: {
            open: 'data-[state=open]:slide-in-from-left',
            close: 'data-[state=closed]:slide-out-to-left',
        },
        top: {
            open: 'data-[state=open]:slide-in-from-top',
            close: 'data-[state=closed]:slide-out-to-top',
        },
        bottom: {
            open: 'data-[state=open]:slide-in-from-bottom',
            close: 'data-[state=closed]:slide-out-to-bottom',
        },
    };
    const sidePositions = {
        right: 'inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm',
        left: 'inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
        top: 'inset-x-0 top-0 h-auto border-b',
        bottom: 'inset-x-0 bottom-0 h-auto border-t',
    };
    return (_jsxs(SheetPortal, { children: [_jsx(SheetOverlay, { disableOpenAnimation: disableOpenAnimation, disableCloseAnimation: disableCloseAnimation }), _jsxs(SheetPrimitive.Content, { "data-slot": "sheet-content", ...(describedBy === 'none' && { 'aria-describedby': undefined }), className: cn('bg-background fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out focus-visible:ring-0 focus-visible:outline-none', !disableOpenAnimation &&
                    'data-[state=open]:animate-in data-[state=open]:duration-500', !disableCloseAnimation &&
                    'data-[state=closed]:animate-out data-[state=closed]:duration-300', sidePositions[side], !disableOpenAnimation && sideAnimations[side].open, !disableCloseAnimation && sideAnimations[side].close, className), ...focusHandlers, ...props, ref: contentRef, children: [children, !hideCloseButton && (_jsxs(SheetPrimitive.Close, { tabIndex: -1, className: "data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none cursor-pointer", children: [_jsx(XCloseIcon, { asButton: false }), _jsx("span", { className: "sr-only", children: t('dialog.close') })] }))] })] }));
}
function SheetHeader({ className, ...props }) {
    return (_jsx("div", { "data-slot": "sheet-header", className: cn('flex flex-col gap-1.5 p-4', className), ...props }));
}
function SheetFooter({ className, ...props }) {
    return (_jsx("div", { "data-slot": "sheet-footer", className: cn('mt-auto flex flex-col gap-2 p-4', className), ...props }));
}
function SheetTitle({ className, ...props }) {
    return (_jsx(SheetPrimitive.Title, { "data-slot": "sheet-title", className: cn('text-foreground font-semibold tracking-tight', className), ...props }));
}
function SheetDescription({ className, ...props }) {
    return (_jsx(SheetPrimitive.Description, { "data-slot": "sheet-description", className: cn('text-muted-foreground text-sm', className), ...props }));
}
export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription, };
//# sourceMappingURL=sheet.js.map